import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import dayjs from "dayjs";
import { EnrollableEdition } from "../hooks/useRescheduleEnrollment";

interface RescheduleEnrollmentModalProps {
  open: boolean;
  editions: EnrollableEdition[];
  isLoadingEditions: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (newEditionId: number) => void;
}

const formatRange = (start?: string, end?: string): string => {
  if (!start && !end) return "";
  const s = start ? dayjs(start) : null;
  const e = end ? dayjs(end) : null;
  if (s && e) return `${s.format("DD/MM/YYYY")} – ${e.format("DD/MM/YYYY")}`;
  return (s ?? e)?.format("DD/MM/YYYY") ?? "";
};

const RescheduleEnrollmentModal: React.FC<RescheduleEnrollmentModalProps> = ({
  open,
  editions,
  isLoadingEditions,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("edition-detail");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const sorted = useMemo(
    () =>
      [...editions].sort(
        (a, b) =>
          new Date(a.startTime ?? 0).getTime() -
          new Date(b.startTime ?? 0).getTime(),
      ),
    [editions],
  );

  const canConfirm = selectedId !== null && !isSubmitting;
  const blockClose = isSubmitting || isLoadingEditions;

  return (
    <Dialog
      open={open}
      onClose={blockClose ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {t("reschedule.title")}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          disabled={blockClose}
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "8px 24px 24px" }}>
        {isLoadingEditions ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : sorted.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              color: theme.palette.text.primary,
              fontSize: "13px",
            }}
          >
            {t("reschedule.empty")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {sorted.map((ed) => {
              const remaining = ed.remainingPlaces ?? 0;
              const disabled = remaining <= 0;
              const selected = selectedId === ed.id;
              return (
                <Box
                  key={ed.id}
                  onClick={() => !disabled && setSelectedId(ed.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: `1px solid ${
                      selected
                        ? theme.palette.primary.main
                        : theme.palette.divider
                    }`,
                    backgroundColor: theme.palette.background.paper,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  <Radio
                    checked={selected}
                    disabled={disabled}
                    onChange={() => setSelectedId(ed.id)}
                    sx={{ p: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {ed.editionName || `#${ed.id}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: theme.palette.text.primary,
                      }}
                    >
                      {[
                        ed.editionCode
                          ? `${t("reschedule.code")}: ${ed.editionCode}`
                          : null,
                        formatRange(ed.startTime, ed.endTime) || null,
                        ed.location || null,
                        t("reschedule.places", { count: remaining }),
                      ]
                        .filter(Boolean)
                        .join(" | ")}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={blockClose}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          {t("reschedule.cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={() => selectedId !== null && onConfirm(selectedId)}
          disabled={!canConfirm}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
        >
          {isSubmitting ? t("reschedule.submitting") : t("reschedule.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleEnrollmentModal;
